/*!

=========================================================
* Light Bootstrap Dashboard React - v2.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/light-bootstrap-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/light-bootstrap-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import Listing from "views/Listing";
import Collection from "views/Collection";
import CollectionDetail from "views/CollectionDetail";
import View3D from "views/View3D";
import EITShop from "views/EITShop";
import EITShopSold from "views/EITShopSold";
import Bot from "views/Bot";
import Dashboard from "views/Dashboard.js";
import UserProfile from "views/UserProfile.js";
import TableList from "views/TableList.js";
import Typography from "views/Typography.js";
import Icons from "views/Icons.js";
import Maps from "views/Maps.js";
import Notifications from "views/Notifications.js";
import Upgrade from "views/Upgrade.js";
import View3DLamp from "views/View3DLamp";
import BoxMaker from "views/BoxMaker";
import { getTranslate, LANGUAGE_KEYS } from "utils";
import { connect } from 'react-redux';
import { getCurrentPlatform } from './utils/platform';

import ScanOrders from "views/ScanOrders";
import OrderList from "views/OrderList";
import SESProductList from './views/SESProduct/List';
import SESProductViewVersions from './views/SESProduct/Versions';
import SESProductQtyChangeList from './views/SESProductQtyChange/List';
import ImportExport from './views/SESProductQtyChange/ImportExport';
import ProductInventoryTransactionList from './views/ProductInventoryTransaction/List';
import ProductInventoryTransactionView from './views/ProductInventoryTransaction/View';
import ProductInventoryTransactionEdit from './views/ProductInventoryTransaction/Edit';
import PermissionList from './views/PermissionManagement/PermissionList';
import UserGroupList from './views/PermissionManagement/UserGroupList';
import UserList from './views/PermissionManagement/UserList';
import MyAccount from './views/PermissionManagement/MyAccount';
import K3DocView from './views/K3Doc/K3DocView';
import K3DocList from './views/K3Doc/K3DocList';
import NewVersionNotification from './views/K3Doc/NewVersionNotification';
import CompanyList from './views/CompanyManagement/List';
import CompanyEdit from './views/CompanyManagement/Edit';
import CompanyView from './views/CompanyManagement/View';
import MyCompany from './views/CompanyManagement/MyCompany';
import ShopInsight from "views/ShopInsight";
import SaleTeamStats from "views/SaleTeamStats";
import FulfillTeamStat from "views/FulfillTeamStat";
import TKT1C3DInfo from "views/TKT1C3DInfo";
import TKT1TrustedPerson from "views/TKT1TrustedPerson";
import TKT1ScanPerson from "views/TKT1ScanPerson";
import TKT1ValidC3D from "views/TKT1ValidC3D";
import TKT1NetworkModem from "views/TKT1NetworkModem";
import TKT1PCInfo from "views/TKT1PCInfo";
import TKT1DevicePack from "views/TKT1DevicePack";
import TKT1Sim from "views/TKT1Sim";
import TKT1BankBrand from "views/TKT1BankBrand";
import TKT1TrustedBankAccount from "views/TKT1TrustedBankAccount";
import TKT1TrustedBankVisa from "views/TKT1TrustedBankVisa";
import TKT1TrustedStory from "views/TKT1TrustedStory";
import TKT1EtsyInfoPack from "views/TKT1EtsyInfoPack";
import TKT1EtsyInfoPackDetail from "views/TKT1EtsyInfoPackDetail";
import TKT1EtsyInfoDevicePack from "views/TKT1EtsyInfoDevicePack";
import TKT1RegisteredEtsyShop from "views/TKT1RegisteredEtsyShop";
import TKT1Email from "views/TKT1Email";
import TKT1FileUploadManagement from "views/TKT1FileUploadManagement";
import TKT1ImageAlbum from "views/TKT1ImageAlbum";
import TKT1Product from "views/TKT1Product";
import TKT1SvgDetail from "views/TKT1SvgDetail";
import TKT1ONMDesignList from "views/TKT1ONMDesignList";
import TKT1ONMDesignDetail from "views/TKT1ONMDesignDetail";
// import SESProductForm from "views/SESProductForm";

// Import Tabler Icons
import {
  IconAd,
  IconChartPie,
  IconShoppingCart,
  IconTruckDelivery,
  IconBuildingFactory2,
  IconTag,
  IconHeart,
  IconRobot,
  IconDashboard,
  IconUser,
  IconCheck,
  IconTable,
  IconTypography,
  IconAtom2,
  IconLayoutGrid,
  IconBox,
  IconLamp,
  IconList,
  IconClockHour4,
  IconScan,
  IconListDetails,
  IconPlus,
  IconEdit,
  IconBell,
  IconBrain,
  IconFlask,
  IconWand,
  IconBuilding,
  IconEye,
  IconRouter,
  IconCreditCard,
  IconBook,
  IconPhoto
} from "@tabler/icons-react";

// User Groups Enum
export const USER_GROUPS = {
  SUPER_ROOT: 'Super Root',
  ROOT: 'Root',
  ROOT_LOWER: 'root', // Support lowercase
  ADMIN: 'Admin',
  RND: 'Team RnD', // Keep for backward compatibility
  SALE: 'Team Sale',
  FULFILL: 'Team Fulfill',
  FACTORY: 'Team Factory',
  BOD: 'BOD',
  C3D_STAFFS: 'C3D_Staffs',
  MEDIA: 'Team Media'
};

// Route Access Levels
export const ROUTE_ACCESS = {
  SUPER_ROOT_ONLY: [USER_GROUPS.SUPER_ROOT],
  ROOT_ONLY: [USER_GROUPS.ROOT],
  C3D_STAFFS: [USER_GROUPS.SUPER_ROOT, USER_GROUPS.ROOT, USER_GROUPS.ADMIN, USER_GROUPS.C3D_STAFFS],
  MEDIA: [USER_GROUPS.SUPER_ROOT, USER_GROUPS.ROOT, USER_GROUPS.ADMIN, USER_GROUPS.MEDIA],
  MEDIA_BOSS: [USER_GROUPS.SUPER_ROOT, USER_GROUPS.ROOT],
  MEDIA_AND_RND: [USER_GROUPS.SUPER_ROOT, USER_GROUPS.ROOT, USER_GROUPS.ADMIN, USER_GROUPS.MEDIA, USER_GROUPS.RND], // Media and RnD
  // ROOT_ONLY: [USER_GROUPS.ROOT, USER_GROUPS.ROOT_LOWER],
  ADMIN_ONLY: [USER_GROUPS.ROOT, USER_GROUPS.ADMIN],
  RND1: [USER_GROUPS.SUPER_ROOT, USER_GROUPS.ROOT, USER_GROUPS.ADMIN], // TKT1 EIT
  RND2: [USER_GROUPS.SUPER_ROOT, USER_GROUPS.ADMIN, USER_GROUPS.RND], // TKT1 RnD
  SALE: [USER_GROUPS.ROOT2, USER_GROUPS.ADMIN, USER_GROUPS.SALE],
  FULFILL: [USER_GROUPS.ROOT2, USER_GROUPS.ADMIN, USER_GROUPS.FULFILL],
  FACTORY: [USER_GROUPS.ROOT2, USER_GROUPS.ADMIN, USER_GROUPS.FACTORY],
  BOD: [USER_GROUPS.ROOT2, USER_GROUPS.ADMIN, USER_GROUPS.BOD],
  USER_ONLY: [USER_GROUPS.SUPER_ROOT, USER_GROUPS.ROOT, USER_GROUPS.ADMIN, USER_GROUPS.RND, USER_GROUPS.RND1, USER_GROUPS.RND2, USER_GROUPS.SALE, USER_GROUPS.FULFILL, USER_GROUPS.FACTORY, USER_GROUPS.BOD],
  ALL_USERS: [USER_GROUPS.SUPER_ROOT, USER_GROUPS.ROOT, USER_GROUPS.ADMIN, USER_GROUPS.RND, USER_GROUPS.RND1, USER_GROUPS.RND2, USER_GROUPS.SALE, USER_GROUPS.FULFILL, USER_GROUPS.FACTORY, USER_GROUPS.BOD, USER_GROUPS.C3D_STAFFS, USER_GROUPS.MEDIA]
};

// View Flags
export const VIEW_FLAGS = {
  LOCALHOST: window.location.hostname.includes("localhost"),
  EIT: true,
  FORCE_VIEW_ECOM: true
};

// Helper function to check if user has access to a route
export const hasAccess = (route, userGroups, user) => {
  // If no allowedUserGroups specified, allow access
  if (!route.allowedUserGroups) return true;
  
  // If user has no groups, deny access
  if (!userGroups || userGroups.length === 0) return false;
  
  // Special case: Hide permissions in TKT1 for non-Root users
  const isTKT1 = getCurrentPlatform() === 'TKT1-landing' || getCurrentPlatform() === 'TKT1-app';
  const isPermissionsRoute = route.path === '/permission-management/permissions';
  const isCurrentUserRoot = userGroups.includes('Root') || userGroups.includes('root') || userGroups.includes('Super Root') || (user && user.is_root === true);
  
  if (isTKT1 && isPermissionsRoute && !isCurrentUserRoot) {
    return false;
  }
  
  // Special case: Users with is_root = true can access ADMIN_ONLY, ROOT_ONLY and SUPER_ROOT_ONLY routes
  if (user && user.is_root === true) {
    const isAdminRoute = route.allowedUserGroups === ROUTE_ACCESS.ADMIN_ONLY;
    const isRootRoute = route.allowedUserGroups === ROUTE_ACCESS.ROOT_ONLY;
    const isSuperRootRoute = route.allowedUserGroups === ROUTE_ACCESS.SUPER_ROOT_ONLY;
    if (isAdminRoute || isRootRoute || isSuperRootRoute) {
      return true;
    }
  }
  
  // Check if user has at least one of the allowed groups
  return userGroups.some(group => route.allowedUserGroups.includes(group));
};


const dashboardRoutes = [
  // AutoEcom RnD Routes
  {
    path: "/rnd-title",
    name: "Smart RnD",
    icon: IconFlask,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.RND2,
    isTitle: true
  },
  {
    path: "/rnd/product-rnd-list",
    name: getTranslate(LANGUAGE_KEYS.TEXT_RND_PRODUCT),
    icon: IconTag,
    component: SESProductList,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.RND2
  },
  {
    path: "/rnd/product-versions",
    name: getTranslate(LANGUAGE_KEYS.TEXT_VERSIONS),
    icon: "nc-icon nc-layers-3",
    component: SESProductViewVersions,
    layout: "/admin",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.RND2
  },
  {
    path: "/rnd/orders",
    name: "Đơn Custom",
    icon: IconListDetails,
    component: OrderList,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.RND2,
    query: { target_page: 'rnd' }
  },
  {
    path: "/rnd/tkt1-product",
    name: "TKT1 Product",
    icon: IconTag,
    component: TKT1Product,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.RND2,
    bottomLine: true
  },
  {
    path: "/rnd/ornament",
    name: "ORNAMENT",
    icon: IconAtom2,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.RND2,
    children: [
      // {
      //   path: "/rnd/ornament/shape",
      //   name: "Shape",
      //   icon: IconAtom2,
      //   component: TKT1ImageAlbum,
      //   layout: "/admin",
      //   allowedUserGroups: ROUTE_ACCESS.RND2,
      //   query: { type: 'ONM_SHAPE' }
      // },
      {
        path: "/rnd/ornament/object",
        name: "SVG",
        icon: IconAtom2, // SVG/Object dùng IconAtom2
        component: TKT1ImageAlbum,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.RND2,
        query: { type: 'ONM_OBJECT' }
      },
      {
        path: "/rnd/ornament/image",
        name: "Image",
        icon: IconPhoto, // Image dùng IconPhoto
        component: TKT1ImageAlbum,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.RND2,
        query: { type: 'ONM_IMAGE' }
      },
      {
        path: "/rnd/svg-detail/:svgId",
        name: "SVG Detail",
        icon: IconAtom2,
        component: TKT1SvgDetail,
        layout: "/admin",
        hidden: true,
        allowedUserGroups: ROUTE_ACCESS.RND2
      },
      {
        path: "/rnd/ornament/design",
        name: "ONM Design",
        icon: IconLayoutGrid,
        component: TKT1ONMDesignList,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.RND2
      },
      {
        path: "/rnd/ornament/design-detail/:designId",
        name: "ONM Design Detail",
        icon: IconLayoutGrid,
        component: TKT1ONMDesignDetail,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.RND2,
        hidden: true
      }
    ]
  },
  {
    path: "/tkt1-media/resource",
    name: "Resource",
    icon: IconBuilding,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.RND2,
    children: [
      {
        path: "/tkt1-media/resource/email-tool",
        name: "Email Tool",
        icon: IconBuilding,
        component: TKT1Email,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.RND2,
        query: { type: 'TOOL' }
      }
    ]
  },
  // Sale Routes
  {
    path: "/sale-title",
    name: "Smart Sale",
    icon: IconShoppingCart,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.SALE,
    isTitle: true
  },
  {
    path: "/sale/orders",
    name: getTranslate(LANGUAGE_KEYS.TEXT_SALE_ORDER_LIST),
    icon: IconListDetails,
    component: OrderList,
    layout: "/admin",
    hidden: VIEW_FLAGS.EIT && !VIEW_FLAGS.FORCE_VIEW_ECOM,
    allowedUserGroups: ROUTE_ACCESS.SALE,
    query: { target_page: 'sale' }
  },
  {
    path: "/sale/product-list",
    name: getTranslate(LANGUAGE_KEYS.TEXT_INVENTORY),
    icon: IconTag,
    component: SESProductList,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.SALE
  },
  {
    path: "/sale/product-sale-list",
    name: getTranslate(LANGUAGE_KEYS.TEXT_SALE_STATS),
    icon: IconChartPie,
    component: SESProductList,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.SALE
  },
  // AutoEcomAds component removed - TKT1 only
  // Fulfill Routes
  {
    path: "/fulfill-title",
    name: "Smart Fulfill",
    icon: IconTruckDelivery,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.FULFILL,
    isTitle: true
  },
  {
    path: "/fulfill/orders",
    name: getTranslate(LANGUAGE_KEYS.TEXT_FULFILL_ORDER_LIST),
    icon: IconListDetails,
    component: OrderList,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.FULFILL,
    query: { target_page: 'fulfill' }
  },
  {
    path: "/rnd/orders",
    name: getTranslate(LANGUAGE_KEYS.TEXT_RND_ORDER_LIST),
    icon: IconListDetails,
    component: OrderList,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.FULFILL,
    query: { target_page: 'rnd' }
  },
  {
    path: "/fulfill/product-list",
    name: getTranslate(LANGUAGE_KEYS.TEXT_INVENTORY),
    icon: IconTag,
    component: SESProductList,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.FULFILL
  },
  {
    path: "/fulfill/import",
    name: getTranslate(LANGUAGE_KEYS.TEXT_IMPORT) || 'Nhập hàng',
    icon: IconPlus,
    component: ImportExport,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.FULFILL
  },
  {
    path: "/fulfill/export",
    name: getTranslate(LANGUAGE_KEYS.TEXT_EXPORT) || 'Xuất hàng',
    icon: IconEdit,
    component: ImportExport,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.FULFILL,
  },
  {
    path: "/fulfill/history",
    name: getTranslate(LANGUAGE_KEYS.TEXT_HISTORY),
    icon: IconList,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.FULFILL,
    children: [
      {
        path: "/fulfill/qty-change",
        name: getTranslate(LANGUAGE_KEYS.TEXT_QUANTITY_CHANGE),
        icon: IconList,
        component: SESProductQtyChangeList,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.FULFILL
      },
      {
        path: "/fulfill/transaction/edit/:id",
        name: getTranslate(LANGUAGE_KEYS.TEXT_EDIT_TRANSACTION),
        icon: IconEdit,
        component: ProductInventoryTransactionEdit,
        layout: "/admin",
        hidden: true,
        allowedUserGroups: ROUTE_ACCESS.FULFILL
      },
      {
        path: "/fulfill/transaction/create",
        name: getTranslate(LANGUAGE_KEYS.TEXT_CREATE_TRANSACTION),
        icon: IconPlus,
        component: ProductInventoryTransactionEdit,
        layout: "/admin",
        hidden: true,
        allowedUserGroups: ROUTE_ACCESS.FULFILL
      },
      {
        path: "/fulfill/transactions",
        name: getTranslate(LANGUAGE_KEYS.TEXT_HISTORY_WAREHOUSE),
        icon: IconList,
        component: ProductInventoryTransactionList,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.FULFILL
      },
      {
        path: "/fulfill/transaction/:id",
        name: getTranslate(LANGUAGE_KEYS.TEXT_HISTORY_DELIVERY),
        icon: IconList,
        component: ProductInventoryTransactionView,
        layout: "/admin",
        hidden: true,
        allowedUserGroups: ROUTE_ACCESS.FULFILL
      },
    ]
  },
  // TKT1 Asset Routes
  {
    path: "/tkt1-asset-title",
    name: "TKT1 Asset",
    icon: IconBuilding,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS,
    isTitle: true
  },
  {
    path: "/tkt1-asset/trusted-story",
    name: "Trusted Story",
    icon: IconBook,
    component: TKT1TrustedStory,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS
  },
  {
    path: "/tkt1-asset/email",
    name: "Email",
    icon: IconBuilding,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS,
    children: [
      {
        path: "/tkt1-asset/email/all",
        name: "All",
        icon: IconBuilding,
        component: TKT1Email,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS,
        query: { type: 'all' }
      },
      {
        path: "/tkt1-asset/email/email1",
        name: "Email 1",
        icon: IconBuilding,
        component: TKT1Email,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS,
        query: { type: 'EMAIL1' }
      },
      {
        path: "/tkt1-asset/email/email2",
        name: "Email 2",
        icon: IconBuilding,
        component: TKT1Email,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS,
        query: { type: 'EMAIL2' }
      },
      {
        path: "/tkt1-asset/email/tool",
        name: "Tool",
        icon: IconBuilding,
        component: TKT1Email,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS,
        query: { type: 'TOOL' }
      },
      {
        path: "/tkt1-asset/email/email-system",
        name: "Email System",
        icon: IconBuilding,
        component: TKT1Email,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS,
        query: { type: 'EMAIL_SYSTEM' }
      }
    ]
  },
  // TKT1 Media Routes
  {
    path: "/tkt1-media-title",
    name: "TKT1 Media",
    icon: IconBuilding,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.MEDIA_BOSS,
    isTitle: true
  },
  {
    path: "/tkt1-media/album-detail/:albumMid",
    name: "Album Detail",
    icon: IconBuilding,
    component: TKT1FileUploadManagement,
    layout: "/admin",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.MEDIA_AND_RND,
    query: { type: 'IMAGE', belong_to_albums: true }
  },
  {
    path: "/tkt1-media/album",
    name: "Album",
    icon: IconBuilding,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.MEDIA,
    children: [
      {
        path: "/tkt1-media/album/all",
        name: "All",
        icon: IconBuilding,
        component: TKT1ImageAlbum,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA_BOSS,
        query: { type: 'all' }
      },
      {
        path: "/tkt1-media/album/listing",
        name: "Listing",
        icon: IconBuilding,
        component: TKT1ImageAlbum,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA,
        query: { type: 'LISTING' }
      },
      {
        path: "/tkt1-media/album/background",
        name: "Background",
        icon: IconBuilding,
        component: TKT1ImageAlbum,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA,
        query: { type: 'BACKGROUND' }
      },
      {
        path: "/tkt1-media/album/decorating",
        name: "Decorating",
        icon: IconBuilding,
        component: TKT1ImageAlbum,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA,
        query: { type: 'DECORATING' }
      },
      {
        path: "/tkt1-media/album/avatar",
        name: "Avatar",
        icon: IconBuilding,
        component: TKT1ImageAlbum,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA,
        query: { type: 'AVATAR' }
      },
      {
        path: "/tkt1-media/album/c3d",
        name: "C3D",
        icon: IconBuilding,
        component: TKT1ImageAlbum,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA_BOSS,
        query: { type: 'C3D' }
      },
      {
        path: "/tkt1-media/album/scan-person",
        name: "Scan Person",
        icon: IconBuilding,
        component: TKT1ImageAlbum,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA_BOSS,
        query: { type: 'SCAN_PERSON' }
      },
      {
        path: "/tkt1-media/album/other-public",
        name: "Other Public",
        icon: IconBuilding,
        component: TKT1ImageAlbum,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA,
        query: { type: 'OTHER_PUBLIC' }
      },
      {
        path: "/tkt1-media/album/other-private",
        name: "Other Private",
        icon: IconBuilding,
        component: TKT1ImageAlbum,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA_BOSS,
        query: { type: 'OTHER_PRIVATE' }
      }
    ]
  },
  {
    path: "/tkt1-media/upload-management",
    name: "Upload",
    icon: IconBuilding,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.MEDIA_BOSS,
    children: [
      {
        path: "/tkt1-media/upload-management/all",
        name: "All",
        icon: IconBuilding,
        component: TKT1FileUploadManagement,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA,
        query: { type: 'all' }
      },
      {
        path: "/tkt1-media/upload-management/image",
        name: "Image",
        icon: IconBuilding,
        component: TKT1FileUploadManagement,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA,
        query: { type: 'IMAGE' }
      },
      {
        path: "/tkt1-media/upload-management/video",
        name: "Video",
        icon: IconBuilding,
        component: TKT1FileUploadManagement,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA,
        query: { type: 'VIDEO' }
      },
      {
        path: "/tkt1-media/upload-management/pdf",
        name: "PDF",
        icon: IconBuilding,
        component: TKT1FileUploadManagement,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA,
        query: { type: 'PDF' }
      },
      {
        path: "/tkt1-media/upload-management/doc",
        name: "Document",
        icon: IconBuilding,
        component: TKT1FileUploadManagement,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA,
        query: { type: 'DOC' }
      },
      {
        path: "/tkt1-media/upload-management/other",
        name: "Other",
        icon: IconBuilding,
        component: TKT1FileUploadManagement,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA,
        query: { type: 'OTHER' }
      }
    ]
  },
  {
    path: "/tkt1-media/resource",
    name: "Resource",
    icon: IconBuilding,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.MEDIA,
    children: [
      {
        path: "/tkt1-media/resource/email-tool",
        name: "Email Tool",
        icon: IconBuilding,
        component: TKT1Email,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.MEDIA,
        query: { type: 'TOOL' }
      }
    ]
  },
  
  // TKT1 Etsy Routes
  {
    path: "/tkt1-etsy-title",
    name: "TKT1 Etsy",
    icon: IconBook,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS,
    isTitle: true
  },
  {
    path: "/tkt1-etsy/etsy-info-pack",
    name: "Etsy Info Pack",
    icon: IconBook,
    component: TKT1EtsyInfoPack,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS
  },
  {
    path: "/tkt1-etsy/etsy-info-pack-detail/:packId",
    name: "Etsy Info Pack Detail",
    icon: IconBook,
    component: TKT1EtsyInfoPackDetail,
    layout: "/admin",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS
  },
  {
    path: "/tkt1-etsy/etsy-info-device-pack",
    name: "Etsy Info Device Pack",
    icon: IconBook,
    component: TKT1EtsyInfoDevicePack,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS
  },
  {
    path: "/tkt1-etsy/registered-shop",
    name: "Registered Shop",
    icon: IconBook,
    component: TKT1RegisteredEtsyShop,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS
  },
  // TKT1 Bank Routes
  {
    path: "/tkt1-bank-title",
    name: "TKT1 Bank",
    icon: IconBuilding,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS,
    isTitle: true
  },
  {
    path: "/tkt1-bank/bank-brand",
    name: "Bank Brand",
    icon: IconBuilding,
    component: TKT1BankBrand,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS
  },
  {
    path: "/tkt1-bank/trusted-bank-account",
    name: "Trusted Bank Acc",
    icon: IconBuilding,
    component: TKT1TrustedBankAccount,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS
  },
  {
    path: "/tkt1-bank/trusted-bank-visa",
    name: "Trusted Bank Visa",
    icon: IconCreditCard,
    component: TKT1TrustedBankVisa,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS
  },
  // TKT1 Person Routes
  {
    path: "/tkt1-title",
    name: "TKT1 Person",
    icon: IconDashboard,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS,
    isTitle: true
  },
  {
    path: "/tkt1/trusted-person",
    name: "Trusted Person",
    icon: IconUser,
    component: TKT1TrustedPerson,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS
  },
  {
    path: "/tkt1/c3d-database",
    name: "C3D Database",
    icon: IconListDetails,
    component: TKT1C3DInfo,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS,
    // hidden: true,
  },
  {
    path: "/tkt1/valid-c3d",
    name: "Valid C3D",
    icon: IconCheck,
    component: TKT1ValidC3D,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS
  },
  {
    path: "/tkt1/scan-person",
    name: "Scan Person",
    icon: IconScan,
    component: TKT1ScanPerson,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS
  },
  // TKT1 Device Routes
  {
    path: "/tkt1-device-title",
    name: "TKT1 Device",
    icon: IconRouter,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS,
    isTitle: true
  },
  {
    path: "/tkt1-device/network-modem",
    name: "Network Modem",
    icon: IconRouter,
    component: TKT1NetworkModem,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS
  },
  {
    path: "/tkt1-device/pc-info",
    name: "PC",
    icon: IconBuilding,
    component: TKT1PCInfo,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS
  },
  {
    path: "/tkt1-device/device-pack",
    name: "Device Pack",
    icon: IconBuilding,
    component: TKT1DevicePack,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS
  },
  {
    path: "/tkt1-device/sim",
    name: "SIM",
    icon: IconBuilding,
    component: TKT1Sim,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.C3D_STAFFS
  },
  // AutoEcom EIT Routes
  {
    path: "/eit-title",
    name: "Smart EIT",
    icon: IconBrain,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.RND1,
    isTitle: true
  },
  {
    path: "/eit/shop",
    name: getTranslate(LANGUAGE_KEYS.TEXT_SHOP),
    icon: IconLayoutGrid,
    component: EITShop,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.RND1,
  },
  {
    path: "/eit/listing",
    name: "Listing",
    icon: IconTag,
    component: Listing,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.RND1,
  },
  {
    path: "/market",
    name: getTranslate(LANGUAGE_KEYS.TEXT_SALE_NGACH),
    icon: IconChartPie,
    component: EITShopSold,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.RND1,
    bottomLine: true
  },
  // AutoEcom BOD Routes
  {
    path: "/bod-title",
    name: "Smart BOD",
    icon: IconAtom2,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.BOD,
    isTitle: true
  },
  {
    path: "/kpi-team",
    name: "KPI Team",
    icon: IconChartPie,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.BOD,
    children: [
      {
        path: "/kpi-team/sale",
        name: "KPI Sale",
        icon: IconChartPie,
        component: SaleTeamStats,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.BOD
      },
      {
        path: "/kpi-team/fulfill",
        name: "KPI Fulfill",
        icon: IconChartPie,
        component: FulfillTeamStat,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.BOD
      }
    ]
  },
  // Super Root Routes
  {
    path: "/super-root-title",
    name: "Smart Super Root",
    icon: IconWand,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY,
    isTitle: true
  },
  {
    path: "/permission-management",
    name: "Quản lý quyền",
    icon: IconAtom2,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY,
    children: [
      {
        path: "/permission-management/permissions",
        name: "Phân quyền",
        icon: IconList,
        component: PermissionList,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
      },
      {
        path: "/permission-management/groups",
        name: "Nhóm người dùng",
        icon: IconList,
        component: UserGroupList,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
      },
      {
        path: "/permission-management/users",
        name: "Quản lý người dùng",
        icon: IconUser,
        component: UserList,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
      },
    ]
  },
  {
    path: "/company-management",
    name: "Quản lý Công ty",
    icon: IconBuilding,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY,
    children: [
      {
        path: "/company-management",  
        name: "Danh sách Công ty",
        icon: IconList,
        component: CompanyList,
        layout: "/admin",
        allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
      },
      {
        path: "/company-management/create",
        name: "Thêm Công ty",
        icon: IconPlus,
        component: CompanyEdit,
        layout: "/admin",
        hidden: true,
        allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
      },
      {
        path: "/company-management/edit/:id",
        name: "Chỉnh sửa Công ty",
        icon: IconEdit,
        component: CompanyEdit,
        layout: "/admin",
        hidden: true,
        allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
      },
      {
        path: "/company-management/view/:id",
        name: "Xem Công ty",
        icon: IconEye,
        component: CompanyView,
        layout: "/admin",
        hidden: true,
        allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
      },
    ]
  },
  // Admin Routes
  {
    path: "/admin-title",
    name: "Smart Admin",
    icon: IconAtom2,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.ADMIN_ONLY,
    isTitle: true
  },
  {
    path: "/admin/users",
    name: "Quản lý người dùng",
    icon: IconUser,
    component: UserList,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.ADMIN_ONLY
  },
  // {
  //   path: '/version-notification',
  //   name: getTranslate(LANGUAGE_KEYS.TEXT_VERSION_NOTIFICATION),
  //   icon: IconBell,
  //   component: NewVersionNotification,
  //   layout: "/admin",
  //   allowedUserGroups: ROUTE_ACCESS.ADMIN_ONLY
  // },
  // Public Routes / Tài khoản
  {
    path: "/my-company",
    name: "Công ty của tôi",
    icon: IconBuilding,
    component: MyCompany,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.ADMIN_ONLY
  },
  {
    path: "/my-account",
    name: "Tài khoản",
    icon: IconUser,
    component: MyAccount,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS,
    bottomLine: true
  },
  // Root Only Routes
  {
    path: "/root-title",
    name: "Smart Root",
    icon: IconRobot,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY,
    isTitle: true
  },
  {
    path: '/k3doc/:id',
    name: getTranslate(LANGUAGE_KEYS.TEXT_K3DOC_VIEW),
    component: K3DocView,
    layout: "/admin",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
  },
  {
    path: '/k3doc',
    name: getTranslate(LANGUAGE_KEYS.TEXT_K3DOC_LIST),
    icon: IconListDetails,
    component: K3DocList,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
  },
  {
    path: "/collection/:id",
    name: getTranslate(LANGUAGE_KEYS.TEXT_COLLECTION_DETAIL),
    icon: IconHeart,
    component: CollectionDetail,
    layout: "/admin",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
  },
  {
    path: "/collection",
    name: getTranslate(LANGUAGE_KEYS.TEXT_COLLECTION),
    icon: IconHeart,
    component: Collection,
    layout: "/admin",
    allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
  },
  {
    path: "/bot",
    name: "Bot",
    icon: IconRobot,
    component: Bot,
    layout: "/admin",
    bottomLine: true,
    allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
  },
  // ============================
  {
    path: "/dashboard",
    name: getTranslate(LANGUAGE_KEYS.TEXT_DASHBOARD),
    icon: IconDashboard,
    component: Dashboard,
    layout: "/admin",
    hidden: !VIEW_FLAGS.LOCALHOST,
    allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
  },
  {
    path: "/user",
    name: getTranslate(LANGUAGE_KEYS.TEXT_USER_PROFILE),
    icon: IconUser,
    component: UserProfile,
    layout: "/admin",
    hidden: !VIEW_FLAGS.LOCALHOST,
    allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
  },
  {
    path: "/table",
    name: getTranslate(LANGUAGE_KEYS.TEXT_TABLE_LIST) || 'Table List',
    icon: IconTable,
    component: TableList,
    layout: "/admin",
    hidden: !VIEW_FLAGS.LOCALHOST,
    allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
  },
  {
    path: "/typography",
    name: getTranslate(LANGUAGE_KEYS.TEXT_TYPOGRAPHY) || 'Typography',
    icon: IconTypography,
    component: Typography,
    layout: "/admin",
    hidden: !VIEW_FLAGS.LOCALHOST,
    allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
  },
  {
    path: "/icons",
    name: getTranslate(LANGUAGE_KEYS.TEXT_ICONS) || 'Icons',
    icon: IconAtom2,
    component: Icons,
    layout: "/admin",
    hidden: !VIEW_FLAGS.LOCALHOST,
    bottomLine: true,
    allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
  },

  // ============================
  // {,
  //   path: "/3d-tool",
  //   name: getTranslate(LANGUAGE_KEYS.TEXT_3D_TOOL),
  //   icon: "nc-icon nc-app",
  //   component: View3D,
  //   layout: "/admin",
  //   hidden: !process.env.REACT_APP_3D_TOOL
  // },
  // {
  //   path: "/box-maker",
  //   name: getTranslate(LANGUAGE_KEYS.TEXT_BOX_MAKER),
  //   icon: IconBox,
  //   component: BoxMaker,
  //   layout: "/admin",
  //   hidden: true,
  //   allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
  // },
  // {
  //   path: "/3d-lamp",
  //   name:  getTranslate(LANGUAGE_KEYS.TEXT_DESIGN_LAMP_TOOL),
  //   icon: IconLamp,
  //   component: View3DLamp,
  //   layout: "/admin",
  //   hidden: true,
  //   allowedUserGroups: ROUTE_ACCESS.SUPER_ROOT_ONLY
  // },
  // {
  //   path: "/upgrade",
  //   name: "Upgrade to PRO",
  //   icon: "nc-icon nc-alien-33",
  //   component: Upgrade,
  //   layout: "/admin"
  // },
  // {
  //   path: "/maps",
  //   name: "Maps",
  //   icon: "nc-icon nc-pin-3",
  //   component: Maps,
  //   layout: "/admin"
  // },
  // {
  //   path: "/notifications",
  //   name: "Notifications",
  //   icon: "nc-icon nc-bell-55",
  //   component: Notifications,
  //   layout: "/admin"
  // }
];

// Create a connected version of the routes that filters based on user groups
const mapStateToProps = (state) => ({
  userGroups: state.authenReducer.user?.groups || state.authenReducer.user?.group_ids || [],
  user: state.authenReducer.user
});

const getFilteredRoutes = (userGroups, user) => {
  return dashboardRoutes.map(route => {
    // If route has children, filter them too
    if (route.children) {
      return {
        ...route,
        children: route.children.filter(child => hasAccess(child, userGroups, user))
      };
    }
    return route;
  }).filter(route => hasAccess(route, userGroups, user));
};

// Export the raw routes for testing and other purposes
export { dashboardRoutes, getFilteredRoutes };

// Export the routes array directly
export default dashboardRoutes;

